import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listSubjects, createSubject } from "@/lib/api";
import { Plus, Heart, Sparkles, Pin, Archive, MoonStar } from "lucide-react";

function since(iso) {
    if (!iso) return "";
    const start = new Date(iso);
    const days = Math.max(0, Math.floor((Date.now() - start.getTime()) / 86400000));
    if (days < 1) return "today";
    if (days === 1) return "1 day";
    if (days < 30) return `${days} days`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} mo`;
    return `${Math.floor(months / 12)} yr`;
}

function fmtDate(iso) {
    return iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";
}

export default function PrayerPage() {
    const [tab, setTab] = useState("active");
    const [sort, setSort] = useState("recent");
    const [data, setData] = useState(null);
    const [adding, setAdding] = useState(false);
    const [newName, setNewName] = useState("");
    const [pinLimitMsg, setPinLimitMsg] = useState("");
    const nav = useNavigate();
    const seq = React.useRef(0);

    const load = React.useCallback(() => {
        const my = ++seq.current;
        const requestedTab = tab;
        listSubjects({ shelf: tab, sort }).then((d) => {
            // Guard against stale responses (tab switched before fetch resolved)
            if (my !== seq.current) return;
            if (d.shelf !== requestedTab) return;
            setData(d);
        });
    }, [tab, sort]);
    useEffect(() => { setData(null); load(); }, [load]);

    const submit = async () => {
        const n = newName.trim();
        if (!n) { setAdding(false); return; }
        const s = await createSubject(n);
        setNewName(""); setAdding(false);
        nav(`/prayer/${s.id}`);
    };

    return (
        <div className="px-5 md:px-8 py-6 md:py-8 pb-32 mx-auto w-full" style={{ maxWidth: "72ch" }}>
            <div className="flex items-center justify-between mb-4 gap-3">
                <h1 className="scripture text-3xl md:text-4xl text-ink tracking-tight">Praying For</h1>
                {tab === "active" && !adding && (
                    <button
                        data-testid="new-prayer-subject"
                        onClick={() => setAdding(true)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-plum text-white font-ui text-[13px] font-medium hover:opacity-90 transition"
                    >
                        <Plus size={16} /> Add
                    </button>
                )}
            </div>

            {adding && (
                <div className="mb-4 flex gap-2">
                    <input
                        autoFocus
                        data-testid="new-prayer-input"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") setAdding(false); }}
                        placeholder="Name"
                        className="flex-1 px-4 py-2 rounded-full bg-white border border-plum/40 font-ui text-[14px] outline-none focus:ring-2 focus:ring-plum/20"
                    />
                    <button data-testid="save-new-prayer" onClick={submit} className="px-4 py-2 rounded-full bg-plum text-white font-ui text-[13px]">Save</button>
                </div>
            )}

            {/* Tabs */}
            <div className="flex items-center gap-2 mb-4">
                <TabBtn testid="tab-active" active={tab === "active"} onClick={() => setTab("active")}>Active</TabBtn>
                <TabBtn testid="tab-kept" active={tab === "kept"} onClick={() => setTab("kept")}>Kept</TabBtn>
                {tab === "active" && (
                    <div className="ml-auto flex items-center gap-2">
                        <label className="font-ui text-[11px] uppercase tracking-[0.15em] text-ink-40">Sort</label>
                        <select
                            data-testid="sort-select"
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            className="font-ui text-[12px] px-2.5 py-1.5 rounded-full bg-white border border-ink-10 outline-none focus:border-plum/40"
                        >
                            <option value="recent">Recently mentioned</option>
                            <option value="longest">Longest carried</option>
                            <option value="az">A–Z</option>
                        </select>
                    </div>
                )}
            </div>

            {pinLimitMsg && (
                <div data-testid="pin-limit-msg" className="mb-3 p-3 rounded-2xl bg-plum/5 border border-plum/20 font-ui text-[13px] text-plum">
                    {pinLimitMsg}
                </div>
            )}

            {!data ? (
                <p className="font-ui text-sm text-ink-40">Loading…</p>
            ) : tab === "active" ? (
                <ActiveView data={data} onReload={load} onLimit={setPinLimitMsg} />
            ) : (
                <KeptView data={data} onReload={load} />
            )}

            <p className="mt-8 font-ui text-[11px] text-ink-40 text-center">Only you can see this list.</p>
        </div>
    );
}

function TabBtn({ testid, active, onClick, children }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={
                "px-4 py-1.5 rounded-full font-ui text-[13px] font-medium transition " +
                (active ? "bg-plum text-white shadow-sm" : "bg-white text-ink-70 border border-ink-10 hover:text-ink")
            }
        >
            {children}
        </button>
    );
}

function ActiveView({ data, onReload, onLimit }) {
    const pinned = data.pinned || [];
    const others = data.others || [];
    const maxPins = data.max_pins || 5;
    return (
        <>
            {pinned.length > 0 && (
                <section data-testid="pinned-section" className="mb-6">
                    <div className="flex items-center gap-2 mb-2 font-ui text-[11px] uppercase tracking-[0.2em] text-plum font-semibold">
                        <Pin size={12} /> Close to my heart <span className="text-ink-40 font-normal">({pinned.length}/{maxPins})</span>
                    </div>
                    <ul className="space-y-2">
                        {pinned.map(s => (
                            <SubjectRow key={s.id} s={s} pinnedSection onReload={onReload} onLimit={onLimit} />
                        ))}
                    </ul>
                </section>
            )}
            {others.length === 0 && pinned.length === 0 ? (
                <div className="mt-10 text-center">
                    <p className="scripture text-[18px] text-ink-70">Who is on your heart?</p>
                    <p className="mt-1 font-ui text-[13px] text-ink-40">Add someone, or type @Name in a journal entry.</p>
                </div>
            ) : (
                <ul className="space-y-2">
                    {others.map(s => <SubjectRow key={s.id} s={s} onReload={onReload} onLimit={onLimit} />)}
                </ul>
            )}
        </>
    );
}

function KeptView({ data, onReload }) {
    const g = data.grouped || {};
    const groups = [
        { key: "answered", label: "Answered", icon: Sparkles, tint: "text-moss-deep" },
        { key: "laid_down", label: "Laid down", icon: MoonStar, tint: "text-slate-600" },
        { key: "manual", label: "Kept", icon: Archive, tint: "text-plum" },
    ];
    const any = groups.some(gp => (g[gp.key] || []).length > 0);
    if (!any) {
        return (
            <p className="mt-6 font-ui text-[14px] text-ink-40 text-center">
                Nothing on the shelf yet. Answered and laid-down subjects gather here after the celebration window.
            </p>
        );
    }
    return (
        <>
            {groups.map(gp => {
                const items = g[gp.key] || [];
                if (items.length === 0) return null;
                const Icon = gp.icon;
                return (
                    <section key={gp.key} data-testid={`kept-group-${gp.key}`} className="mb-6">
                        <div className={"flex items-center gap-2 mb-2 font-ui text-[11px] uppercase tracking-[0.2em] font-semibold " + gp.tint}>
                            <Icon size={12} /> {gp.label} <span className="text-ink-40 font-normal">({items.length})</span>
                        </div>
                        <ul className="space-y-2">
                            {items.map(s => <SubjectRow key={s.id} s={s} kept onReload={onReload} />)}
                        </ul>
                    </section>
                );
            })}
        </>
    );
}

function SubjectRow({ s, pinnedSection, kept, onReload, onLimit }) {
    const nav = useNavigate();
    const doPin = async (e) => {
        e.preventDefault(); e.stopPropagation();
        try {
            const { togglePin } = await import("@/lib/api");
            await togglePin(s.id);
            onLimit?.("");
            onReload?.();
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not pin.";
            onLimit?.(msg);
        }
    };
    const doReturn = async (e) => {
        e.preventDefault(); e.stopPropagation();
        const { returnToActive } = await import("@/lib/api");
        await returnToActive(s.id);
        onReload?.();
    };

    const to = `/prayer/${s.id}`;
    return (
        <li>
            <Link
                to={to}
                data-testid={`prayer-subject-${s.id}`}
                className="flex items-center justify-between p-4 rounded-2xl bg-white border border-ink-10 hover:border-plum/40 hover:shadow-md transition"
            >
                <div className="min-w-0">
                    <div className="scripture text-[17px] text-ink font-medium">{s.name}</div>
                    <div className="mt-0.5 font-ui text-[12px] text-ink-40">
                        {kept
                            ? (s.shelf_reason === "answered"
                                ? <>Answered {fmtDate(s.answered_at)}{s.answered_note ? ` · “${s.answered_note}”` : ""}</>
                                : s.shelf_reason === "laid_down"
                                    ? <>Laid down {fmtDate(s.laid_down_at)}</>
                                    : <>Moved {fmtDate(s.moved_to_kept_at)}</>)
                            : (s.status === "answered"
                                ? <>Answered {fmtDate(s.answered_at)}</>
                                : s.status === "laid_down"
                                    ? <>Laid down {fmtDate(s.laid_down_at)}</>
                                    : <>Praying · {since(s.started_at)}{s.entry_count ? ` · ${s.entry_count} ${s.entry_count === 1 ? "entry" : "entries"}` : ""}</>)}
                    </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    {!kept && (
                        <button
                            data-testid={`pin-toggle-${s.id}`}
                            onClick={doPin}
                            aria-pressed={!!s.pinned}
                            aria-label={s.pinned ? "Unpin" : "Pin"}
                            className={
                                "w-9 h-9 rounded-full flex items-center justify-center transition " +
                                (s.pinned
                                    ? "bg-plum text-white"
                                    : "text-ink-40 hover:text-plum hover:bg-plum/10")
                            }
                        >
                            <Pin size={14} />
                        </button>
                    )}
                    {kept ? (
                        <button
                            data-testid={`return-active-${s.id}`}
                            onClick={doReturn}
                            className="px-2.5 py-1.5 rounded-full font-ui text-[11px] uppercase tracking-[0.14em] font-semibold bg-plum/10 text-plum hover:bg-plum/20 transition"
                        >
                            Return
                        </button>
                    ) : (
                        s.status === "answered" ? (
                            <span data-testid={`badge-${s.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-moss/10 text-moss-deep font-ui text-[10px] font-semibold uppercase tracking-[0.14em]">
                                <Sparkles size={12} /> Answered
                            </span>
                        ) : s.status === "laid_down" ? (
                            <span data-testid={`badge-${s.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-ui text-[10px] font-semibold uppercase tracking-[0.14em]">
                                <MoonStar size={12} /> Laid down
                            </span>
                        ) : (
                            <span data-testid={`badge-${s.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-plum/10 text-plum font-ui text-[10px] font-semibold uppercase tracking-[0.14em]">
                                <Heart size={12} /> Praying
                            </span>
                        )
                    )}
                </div>
            </Link>
        </li>
    );
}
